import { Trans } from '@lingui/react/macro';

import { Button } from '@/components/base/buttons/button';
import { Tooltip, TooltipTrigger } from '@/components/base/tooltip/tooltip';
import { formatTime } from '@/utils/date';

interface ApplyButtonProps {
  isAuthenticated: boolean;
  isAtScreenerLimit: boolean;
  canApply: boolean;
  isDraft: boolean;
  isDisabled: boolean;
  showSignupToApply?: boolean;
  cycleExpiryDate?: string;
  onClick: () => void;
  onPlaidVerification: () => void;
}

export function ApplyButton({
  isAuthenticated,
  isAtScreenerLimit,
  canApply,
  isDraft,
  isDisabled,
  showSignupToApply = false,
  cycleExpiryDate,
  onClick,
}: ApplyButtonProps) {
  if (isAuthenticated && isAtScreenerLimit) {
    return (
      <Tooltip
        title={<Trans>Daily limit reached</Trans>}
        description={
          cycleExpiryDate ? (
            <Trans>You can apply again at {formatTime(cycleExpiryDate)}</Trans>
          ) : (
            <Trans>Please try again later</Trans>
          )
        }
        placement="top"
      >
        <TooltipTrigger>
          <Button
            color="primary"
            size="lg"
            isDisabled
            className="cursor-help"
            data-testid="participant-project-apply-button"
          >
            {isDraft ? <Trans>Resume screener</Trans> : <Trans>Apply</Trans>}
          </Button>
        </TooltipTrigger>
      </Tooltip>
    );
  }

  return (
    <Button
      color="primary"
      size="lg"
      isDisabled={isDisabled}
      data-testid="participant-project-apply-button"
      onClick={() => {
        onClick();
      }}
    >
      <ApplyButtonLabel
        isAuthenticated={isAuthenticated}
        canApply={canApply}
        isDraft={isDraft}
        showSignupToApply={showSignupToApply}
      />
    </Button>
  );
}

function ApplyButtonLabel({
  isAuthenticated,
  canApply,
  isDraft,
  showSignupToApply,
}: {
  isAuthenticated: boolean;
  canApply: boolean;
  isDraft: boolean;
  showSignupToApply: boolean;
}) {
  if (!isAuthenticated) {
    return showSignupToApply ? (
      <Trans>Sign up to apply</Trans>
    ) : (
      <Trans>Sign in to apply</Trans>
    );
  }
  if (!canApply) {
    return <Trans>Not eligible</Trans>;
  }
  if (isDraft) {
    return <Trans>Resume screener</Trans>;
  }
  return <Trans>Apply</Trans>;
}
